// client/src/intermission/IntermissionManager.js
import Phaser from 'phaser';
import { computeWinChances } from '../Utils';
import { IntermissionOverlay } from './IntermissionOverlay';
// Debug flag (?debugCam=1 also enables camera logs). Use ?debugInter=1 to log intermission transitions
let INTER_DEBUG = false;
try {
  const qs = new URLSearchParams(window.location.search);
  INTER_DEBUG = (qs.get('debugInter') === '1') || (localStorage.getItem('debugInter') === '1') || (qs.get('debugCam') === '1');
} catch (_) {}

export class IntermissionManager {
  constructor(scene, intermissionsData, itemsData, itemsById) {
    this.scene = scene;
    this.intermissionsData = intermissionsData || [];
    this.itemsData = itemsData || [];
    this.itemsById = itemsById || new Map();

    this.intermissionLoading = false;
    this.intermissionActive = false;
    this.intermissionResumeDelayMs = 0;

    this.screenArmed = new Map(); // id -> boolean (edge-triggered, re-arming)
    this.predictionVotes = new Map(); // playerId -> weight
    this.predictedChances = new Map(); // playerId -> percent (0..100)

    this.overlay = null;
    this.currentMusic = null;
    this.battleMusicPrevVolume = null;

    // Track elapsed battle time (in ms)
    this.battleElapsedMs = 0;
  }

  isPaused() {
    return this.intermissionLoading || this.intermissionActive || this.intermissionResumeDelayMs > 0;
  }

  update(delta, aliveCount, players) {
    // Check if match is over - if so, destroy any active intermissions and stop processing
    if (this.scene.gameState === 'ended') {
      if (this.intermissionActive || this.intermissionLoading) {
        console.log('🏁 Match ended - destroying active intermission');
        this.endIntermission();
      }
      return; // Stop processing intermissions when match is over
    }

    // Accumulate battle time when not paused
    if (!this.isPaused()) {
      this.battleElapsedMs += delta;
    }
    if (this.intermissionResumeDelayMs > 0) {
      this.intermissionResumeDelayMs = Math.max(0, this.intermissionResumeDelayMs - delta);
    }
    // Reflect flags onto scene to keep existing guards simple
    this.scene.intermissionLoading = this.intermissionLoading;
    this.scene.intermissionActive = this.intermissionActive;
    this.scene.intermissionResumeDelayMs = this.intermissionResumeDelayMs;

    if (this.isPaused()) return;
    if (!this.intermissionsData.length) return;

    // Don't process intermissions until at least 2 seconds has passed in the battle
    // This allows boundary and camera initialization to complete
    if (this.battleElapsedMs < 2000) return;

    // Highest priority first
    const ordered = [...this.intermissionsData].sort((a, b) => (b.priority || 0) - (a.priority || 0));
    for (const screen of ordered) {
      const id = screen.id;
      if (!this.screenArmed.has(id)) this.screenArmed.set(id, true);
      const armed = this.screenArmed.get(id);
      const ok = this.evaluateTrigger(screen.trigger, aliveCount);
      if (armed && ok) {
        this.screenArmed.set(id, false);
        this.startIntermission(screen, players);
        break;
      }
      if (!ok) this.screenArmed.set(id, true);
    }
  }

  evaluateTrigger(trigger, aliveCount) {
    if (!trigger) return false;
    // Support both canonical shape { type, operator, value }
    // and shorthand shape { type, lte: N } | { type, gte: N } | { type, eq: N }
    let v = Number(trigger.value ?? 0);
    let op = trigger.operator;
    if (!op) {
      if (typeof trigger.lte !== 'undefined') { op = 'lte'; v = Number(trigger.lte); }
      else if (typeof trigger.gte !== 'undefined') { op = 'gte'; v = Number(trigger.gte); }
      else if (typeof trigger.eq !== 'undefined') { op = 'eq'; v = Number(trigger.eq); }
    }

    if (trigger.type === 'players_remaining') {
      switch (op) {
        case 'lte': return aliveCount <= v;
        case 'gte': return aliveCount >= v;
        case 'eq':  return aliveCount === v;
        default: return false;
      }
    }

    if (trigger.type === 'battle_time_seconds') {
      const seconds = Math.floor(this.battleElapsedMs / 1000);
      switch (op) {
        case 'lte': return seconds <= v;
        case 'gte': return seconds >= v;
        case 'eq':  return seconds === v;
        default: return false;
      }
    }

    return false;
  }

  startIntermission(screen, players) {
    this.intermissionLoading = true;
    if (INTER_DEBUG) console.log('[Intermission] start loading', { screenId: screen?.id, now: Date.now() });

    this.scene.time.delayedCall(1000, () => {
      this.intermissionLoading = false;
      this.intermissionActive = true;
      if (INTER_DEBUG) console.log('[Intermission] active', { screenId: screen?.id, now: Date.now() });

      // Lower battle music volume during intermission (do not pause)
      if (this.scene.battleMusic &&
          typeof this.scene.battleMusic.volume !== 'undefined' &&
          this.scene.battleMusic.scene === this.scene) {
        try {
          // Respect user-set base volume if present
          const baseVol = (typeof this.scene.battleMusicUserVolume === 'number' && this.scene.battleMusicUserVolume > 0)
            ? this.scene.battleMusicUserVolume
            : this.scene.battleMusic.volume;
          this.battleMusicPrevVolume = baseVol;
          // If user has music muted (volume 0), keep it muted; otherwise dip to 0.15
          if ((this.scene.battleMusic.volume || 0) === 0) {
            this.scene.battleMusic.setVolume(0);
          } else {
            this.scene.battleMusic.setVolume(0.15);
          }
        } catch (error) {
          console.warn('Failed to lower battle music volume during intermission:', error);
          this.battleMusicPrevVolume = null;
        }
      } else {
        this.battleMusicPrevVolume = null;
      }

      // Start intermission music if specified (load on demand)
      const musicKey = screen.music;
      if (musicKey && this.scene.sound) {
        const tryPlay = () => {
          if (this.currentMusic && this.currentMusic.isPlaying) {
            this.currentMusic.stop();
            this.currentMusic.destroy();
            this.currentMusic = null;
          }
          const s = this.scene.sound.get(musicKey) || this.scene.sound.add(musicKey);
          if (s) {
            this.currentMusic = s;
            this.currentMusic.setLoop(false);
            // Respect user's music volume/mute setting
            const userVol = (typeof this.scene.battleMusicUserVolume === 'number') ? this.scene.battleMusicUserVolume : 0.315;
            const wasMuted = (typeof this.battleMusicPrevVolume === 'number') ? (this.battleMusicPrevVolume === 0) : false;
            const startVol = wasMuted ? 0 : userVol;
            this.currentMusic.setVolume(startVol);
            this.currentMusic.play();
          }
        };

        if (this.scene.cache.audio && this.scene.cache.audio.exists(musicKey)) {
          tryPlay();
        } else {
          this.scene.load.once('complete', () => {
            if (this.intermissionActive) tryPlay();
          });

          // Determine S3 base path for any theme; fallback to local assets if unavailable
          const theme = this.scene.getCurrentTheme?.() || {};
          const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
          const s3BasePath = theme?.s3BasePath || (theme?.id ? `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${theme.id}` : '');
          const useS3 = !!s3BasePath;
          const musicPath = useS3
            ? `${s3BasePath}/${String(musicKey).includes('/') ? musicKey : `music/${musicKey}`}`
            : `assets/${musicKey}`;

          console.log(`🎵 IntermissionManager: loading music`, { musicKey, musicPath, useS3 });
          this.scene.load.audio(musicKey, musicPath);
          this.scene.load.start();
        }
      }

      // Compute chances for action list
      let predicted = null;
      if (screen.content?.type === 'playerListWithAction') {
        predicted = computeWinChances(players.filter(p => p.isAlive), this.itemsById, { x: 1024, y: 768 });
        this.predictedChances = predicted;
      }

      // Use static import instead of dynamic import to avoid timing issues
      if (this.overlay) this.overlay.destroy();
      this.overlay = new IntermissionOverlay(this.scene);
      this.overlay.show(screen, players, this.itemsById, predicted);

      // Start simulated prediction voting for action screens
      if (screen.content?.type === 'playerListWithAction') {
        this.simulatePredictionStream();
      }

      const durMs = (screen.durationSeconds || 5) * 1000;
      this.scene.time.delayedCall(durMs, () => this.endIntermission());
    });
  }

  endIntermission() {
    this.intermissionActive = false;
    if (INTER_DEBUG) console.log('[Intermission] end', { now: Date.now() });
    if (this.overlay) {
      this.overlay.destroy();
      this.overlay = null;
    }
    // Stop any intermission music
    if (this.currentMusic) {
      if (this.currentMusic.isPlaying) this.currentMusic.stop();
      this.currentMusic.destroy();
      this.currentMusic = null;
    }
    // Restore battle music volume with a fade if we lowered it
      if (this.scene.battleMusic != null && this.battleMusicPrevVolume != null) {
      const targetVol = Math.max(0, Math.min(1, this.battleMusicPrevVolume));

      // Additional safety check for battleMusic validity
      if (!this.scene.battleMusic ||
          typeof this.scene.battleMusic.volume === 'undefined' ||
          !this.scene.battleMusic.scene ||
          this.scene.battleMusic.scene !== this.scene) {
        console.warn('battleMusic object is in invalid state, skipping volume restoration');
        this.battleMusicPrevVolume = null;
        return;
      }

      try {
        if (this.scene.battleMusic.volume !== targetVol) {
          this.scene.tweens.add({
            targets: this.scene.battleMusic,
            volume: targetVol,
            duration: 1200,
            ease: 'Cubic.easeOut'
          });
        } else {
          this.scene.battleMusic.setVolume(targetVol);
        }
      } catch (error) {
        console.warn('Failed to restore battle music volume:', error);
        // Fallback: try to set volume directly if tween fails
        try {
          if (this.scene.battleMusic && typeof this.scene.battleMusic.setVolume === 'function') {
            this.scene.battleMusic.setVolume(targetVol);
          }
        } catch (fallbackError) {
          console.warn('Fallback volume setting also failed:', fallbackError);
        }
      }
      this.battleMusicPrevVolume = null;
    }
    // Ask camera to restore baseline framing after overlays disappear
    try {
      if (this.scene.cameraController && typeof this.scene.cameraController.restoreBaselineZoomAndCenter === 'function') {
        this.scene.cameraController.restoreBaselineZoomAndCenter();
      }
    } catch (_) {}
    // Extra resume delay for boundary/world
    this.intermissionResumeDelayMs = 500;
  }

  registerPredictionVote(playerId, weight) {
    const w = Math.max(0, Number(weight) || 0);
    if (w <= 0) return;
    const cur = this.predictionVotes.get(playerId) || 0;
    this.predictionVotes.set(playerId, cur + w);

    if (!this.intermissionActive) return;

    const alive = this.scene.players.filter(p => p.isAlive);
    const base = computeWinChances(alive, this.itemsById, { x: 1024, y: 768 });

    // Bias with votes (log scale, cap influence)
    let total = 0;
    const biased = new Map();
    alive.forEach(p => {
      const id = p.walletAddress || p.name;
      const basePct = base.get(id) || 0;
      const votes = this.predictionVotes.get(id) || 0;
      const adj = basePct + Math.min(50, Math.log2(1 + votes) * 5);
      biased.set(id, adj);
      total += adj;
    });

    alive.forEach(p => {
      const id = p.walletAddress || p.name;
      const pct = Math.round(((biased.get(id) || 0) / (total || 1)) * 100);
      this.predictedChances.set(id, pct);
      if (this.overlay) this.overlay.updatePercent(id, pct);
    });
  }

  simulatePredictionStream() {
    if (this._simEvt) this._simEvt.remove(false);
    this._simEvt = this.scene.time.addEvent({
      delay: 800,
      loop: true,
      callback: () => {
        if (!this.intermissionActive) {
          if (this._simEvt) this._simEvt.remove(false);
          return;
        }
        const alive = this.scene.players.filter(p => p.isAlive);
        if (!alive.length) return;
        const pick = Phaser.Utils.Array.GetRandom(alive);
        const id = pick.walletAddress || pick.name;
        const w = Phaser.Math.Between(1, 3);
        this.registerPredictionVote(id, w);
      }
    });
  }
}
